import { useIsFocused } from '@react-navigation/native'
import { SearchResponse } from 'algoliasearch/lite'
import React, { useMemo } from 'react'
import { ScrollViewProps, View, ViewToken } from 'react-native'
import { IOScrollView } from 'react-native-intersection-observer'
import styled from 'styled-components/native'

import { SearchGroupNameEnumv2 } from 'api/gen'
import { useAccessibilityFiltersContext } from 'features/accessibility/context/AccessibilityFiltersWrapper'
import { usePreviousRouteName } from 'features/navigation/helpers/usePreviousRouteName'
import { VenuePlaylist } from 'features/search/components/VenuePlaylist/VenuePlaylist'
import { useSearch } from 'features/search/context/SearchWrapper'
import { getSearchVenuePlaylistTitle } from 'features/search/helpers/getSearchVenuePlaylistTitle/getSearchVenuePlaylistTitle'
import { gridListLayoutActions, useGridListLayout } from 'features/search/store/gridListLayoutStore'
import { GridListLayout, SearchView, VenuesUserData } from 'features/search/types'
import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { AlgoliaVenueOfferListItem } from 'libs/algolia/types'
import { analytics } from 'libs/analytics/provider'
import { useLocation } from 'libs/location/location'
import { LocationMode } from 'libs/location/types'
import { GeolocationBanner } from 'shared/Banners/GeolocationBanner'
import { NumberOfItems } from 'shared/NumberOfItems/NumberOfItems'
import { ObservedPlaylist } from 'shared/ObservedPlaylist/ObservedPlaylist'
import { Offer } from 'shared/offer/types'
import { GridLayoutButton } from 'ui/components/buttons/GridLayoutButton'
import { ListLayoutButton } from 'ui/components/buttons/ListLayoutButton'
import { AIFakeDoorBanner } from 'ui/components/ModuleBanner/AIFakeDoorBanner'
import { Banner } from 'ui/designSystem/Banner/Banner'
import { Error } from 'ui/svg/icons/Error'
import { Typo } from 'ui/theme'

interface SearchListHeaderProps extends ScrollViewProps {
  nbHits: number
  hasOfferHits?: boolean
  userData: SearchResponse<Offer[]>['userData']
  venues?: AlgoliaVenueOfferListItem[]
  venuesUserData: VenuesUserData
  artistSection?: React.ReactNode
  shouldDisplayGridList?: boolean
  onViewableVenuePlaylistItemsChanged?: (
    items: Pick<ViewToken, 'key' | 'index'>[],
    moduleId: string,
    itemType: 'offer' | 'venue' | 'artist' | 'unknown',
    playlistIndex?: number
  ) => void
  enableAIFakeDoor?: boolean
  onPressAIFakeDoorBanner: () => void
}

export const SearchListHeader: React.FC<SearchListHeaderProps> = ({
  nbHits,
  hasOfferHits = true,
  userData,
  venues,
  venuesUserData,
  artistSection,
  shouldDisplayGridList,
  onViewableVenuePlaylistItemsChanged,
  enableAIFakeDoor,
  onPressAIFakeDoorBanner,
}) => {
  const { geolocPosition, showGeolocPermissionModal, selectedLocationMode } = useLocation()
  const { disabilities } = useAccessibilityFiltersContext()
  const {
    searchState: { venue, offerCategories },
  } = useSearch()
  const isFocused = useIsFocused()

  const isLocated = useMemo(
    () => selectedLocationMode !== LocationMode.EVERYWHERE,
    [selectedLocationMode]
  )

  const shouldDisplayAvailableUserDataMessage = userData?.length > 0
  const unavailableOfferMessage = shouldDisplayAvailableUserDataMessage ? userData[0]?.message : ''
  const shouldDisplayAccessibilityContent = Object.values(disabilities).filter(Boolean).length > 0
  const venuePlaylistTitle = getSearchVenuePlaylistTitle(
    shouldDisplayAccessibilityContent,
    venuesUserData?.[0]?.venue_playlist_title,
    isLocated
  )

  const previousRouteName = usePreviousRouteName()

  const selectedGridListLayout = useGridListLayout()

  const offerTitle = `Les offres${shouldDisplayAccessibilityContent ? ' dans des lieux accessibles' : ''}`

  const shouldDisplayVenuesPlaylist =
    !venue && !!venues?.length && previousRouteName !== SearchView.Thematic

  const onPress = () => {
    void analytics.logActivateGeolocfromSearchResults()
    showGeolocPermissionModal()
  }

  const shouldDisplayGeolocationBanner =
    geolocPosition === null &&
    offerCategories?.[0] !== SearchGroupNameEnumv2.EVENEMENTS_EN_LIGNE &&
    nbHits > 0 &&
    !shouldDisplayAvailableUserDataMessage

  const onGridListButtonPress = (layout: GridListLayout) => {
    gridListLayoutActions.setLayout(layout)
    void analytics.logHasClickedGridListToggle({ fromLayout: selectedGridListLayout })
  }

  const getLayoutButtonProps = (layout: GridListLayout) => ({
    layout,
    isSelected: selectedGridListLayout === layout,
    onPress: () => onGridListButtonPress(layout),
  })

  const handleVenuePlaylistViewableItemsChanged = React.useCallback(
    (items: Pick<ViewToken, 'key' | 'index'>[]) => {
      if (!isFocused) return
      onViewableVenuePlaylistItemsChanged?.(items, 'searchResultsVenuePlaylist', 'venue', 0)
    },
    [isFocused, onViewableVenuePlaylistItemsChanged]
  )

  return (
    <View testID="searchListHeader">
      {enableAIFakeDoor ? (
        <AIFakeDoorBannerContainer>
          <AIFakeDoorBanner onPress={onPressAIFakeDoorBanner} />
        </AIFakeDoorBannerContainer>
      ) : null}
      {shouldDisplayGeolocationBanner ? (
        <GeolocationBannerContainer>
          <GeolocationBanner
            title="Géolocalise-toi"
            subtitle="Pour trouver des offres autour de toi"
            analyticsFrom="search"
            onPress={onPress}
          />
        </GeolocationBannerContainer>
      ) : null}
      {shouldDisplayAvailableUserDataMessage ? (
        <BannerOfferNotPresentContainer
          testID="banner-container"
          accessibilityRole={AccessibilityRole.STATUS}
          nbHits={nbHits}>
          <Banner label={unavailableOfferMessage} Icon={Error} />
        </BannerOfferNotPresentContainer>
      ) : null}
      {artistSection}
      {shouldDisplayVenuesPlaylist ? (
        <IOScrollView>
          <ObservedPlaylist onViewableItemsChanged={handleVenuePlaylistViewableItemsChanged}>
            {({ listRef, handleViewableItemsChanged }) => (
              <StyledVenuePlaylist
                venuePlaylistTitle={venuePlaylistTitle}
                venues={venues}
                isLocated={isLocated}
                playlistRef={listRef}
                onViewableItemsChanged={handleViewableItemsChanged}
              />
            )}
          </ObservedPlaylist>
        </IOScrollView>
      ) : null}
      {hasOfferHits ? (
        <HeaderSectionContainer>
          <TitleContainer>
            <Title>{offerTitle}</Title>
            <NumberOfItems nbItems={nbHits} />
          </TitleContainer>
          {shouldDisplayGridList ? (
            <GridListMenu testID="grid-list-menu">
              <ListLayoutButton {...getLayoutButtonProps(GridListLayout.LIST)} />
              <GridLayoutButton {...getLayoutButtonProps(GridListLayout.GRID)} />
            </GridListMenu>
          ) : null}
        </HeaderSectionContainer>
      ) : null}
    </View>
  )
}

const GridListMenu = styled(View)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  marginRight: theme.designSystem.size.spacing.xl,
  marginTop: theme.designSystem.size.spacing.l,
}))

const HeaderSectionContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
})

const TitleContainer = styled.View(({ theme }) => ({
  flex: 1,
  flexDirection: 'column',
  marginHorizontal: theme.designSystem.size.spacing.xl,
}))

const GeolocationBannerContainer = styled.View(({ theme }) => ({
  marginVertical: theme.designSystem.size.spacing.l,
  marginHorizontal: theme.contentPage.marginHorizontal,
}))

const BannerOfferNotPresentContainer = styled.View<{ nbHits: number }>(({ nbHits, theme }) => ({
  paddingHorizontal: theme.designSystem.size.spacing.xl,
  ...(nbHits > 0 && { paddingBottom: theme.designSystem.size.spacing.l }),
}))

const Title = styled(Typo.Title3)(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.l,
}))

const StyledVenuePlaylist = styled(VenuePlaylist)(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.l,
}))

const AIFakeDoorBannerContainer = styled.View(({ theme }) => ({
  marginHorizontal: theme.contentPage.marginHorizontal,
  marginTop: theme.designSystem.size.spacing.l,
}))
